import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { flushSync } from "react-dom";
import {
    createRealtimeVoiceEngine,
    SupportedLanguage,
    getProviderByLanguage,
    WordBoundary,
} from "../../../services/realtimeVoice";
import { getUserDisplayName } from "../../../utils/index";
import { UIMsg } from "../types";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";
import { isAuthenticated, logout, loadAuthUser } from "../../../services/auth";

type EngineStatus = "idle" | "recognizing" | "speakingTTS" | "silent";

interface UseWhisperEngineProps {
    language: SupportedLanguage;
    onNewMessage: (msg: UIMsg[]) => void;
    onStopConversation?: () => void;
    onDurationLimit?: (msg: string) => void;
    onSessionReady?: (id?: string) => void;
    onUnmount?: (buffered: { userText: string; aiText: string }) => void;
    onAISpeakingStateChange?: (isAISpeaking: boolean) => void;
    onSuggestions?: (suggestions: string[]) => void;
}

export function useWhisperEngine({ 
    language, 
    onNewMessage, 
    onStopConversation, 
    onDurationLimit, 
    onSessionReady, 
    onUnmount,
    onAISpeakingStateChange,
    onSuggestions
}: UseWhisperEngineProps) {
    const { t } = useAppSettings();
    const navigate = useNavigate();

    // States
    const [status, setStatus] = useState<EngineStatus>("idle");
    const [lastText, setLastText] = useState("");
    const [isConnecting, setIsConnecting] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [conversationActive, setConversationActive] = useState(false);
    const [isUserSpeaking, setIsUserSpeaking] = useState(false);

    // Typing Animation Refs & State
    const targetAiReplyTextRef = useRef("");
    const [displayedAiReplyText, setDisplayedAiReplyText] = useState("");
    const [isAISpeaking, setIsAISpeaking] = useState(false);
    const [suggestions, setSuggestions] = useState<string[]>([]);

    // Word Timing Queue
    const wordMarkersQueueRef = useRef<WordBoundary[]>([]);

    // Refs
    const sessionStartTimeRef = useRef<number | null>(null);
    const smoothedPlaybackTimeMsRef = useRef(0);
    const onUnmountRef = useRef(onUnmount);

    useEffect(() => {
        onUnmountRef.current = onUnmount;
    }, [onUnmount]);

    // Engine Initialization
    const realtimeVoiceEngineRef = useRef(createRealtimeVoiceEngine(
        setLastText,
        undefined, // setAudioLevel not used in UI currently?
        onNewMessage as any, // Cast to avoid Dispatch<SetStateAction> mismatch since we only pass value
        () => setLastText(""),
        (text) => {
            setAiReplyText(text);
        }
    )).current;

    // Typing Animation Loop
    useEffect(() => {
        let animationFrameId: number;
        
        const animateTyping = () => {
            setDisplayedAiReplyText(current => {
                const targetText = targetAiReplyTextRef.current;
                
                // --- 虚拟平滑时钟逻辑 (Smoothing Clock) ---
                // 获取物理时钟进度 (Raw Clock)
                const rawPlaybackTimeMs = (realtimeVoiceEngineRef.audioManager?.getPlaybackTime() || 0) * 1000;
                
                // 核心门控：只有当音频启动（isAISpeaking=true）或者音频已结束但队列有残留时，才推进时钟
                // 这样可以彻底解决“音频还没出来，文字先抢跑”导致的初始跳变问题
                if (isAISpeaking) {
                    const diff = rawPlaybackTimeMs - smoothedPlaybackTimeMsRef.current;
                    if (diff > 0) {
                        // 遇到元数据爆发式到达时，每帧最多追赶 100ms，平滑化弹出过程
                        smoothedPlaybackTimeMsRef.current += Math.min(diff, 100); 
                    }
                } else if (wordMarkersQueueRef.current.length > 0 && current.length > 0) {
                    // 如果音频已停（断句或结束）但队列还有余留，且已经开始打字了，以匀速走完
                    smoothedPlaybackTimeMsRef.current += 20; 
                }

                const playbackTimeMs = smoothedPlaybackTimeMsRef.current;
                let nextText = current;
                let wordsPoppedInThisFrame = 0;

                // --- 单词弹出逻辑 (Audio Stream Driven with Burst Control) ---
                while (wordMarkersQueueRef.current.length > 0) {
                    const first = wordMarkersQueueRef.current[0];
                    if (playbackTimeMs >= first.offset) {
                        // 单词时刻已到。
                        // 核心限制：即使时钟大幅领先，每帧也最多弹出 2 个单词，确保视觉连贯性。
                        if (wordsPoppedInThisFrame >= 2) break; 
                        
                        const word = wordMarkersQueueRef.current.shift()!;
                        wordsPoppedInThisFrame++;
                        
                        // 从 targetText 中同步下一个片段
                        if (targetText.length > nextText.length) {
                            const nextSegment = targetText.slice(nextText.length);
                            const wordIndex = nextSegment.indexOf(word.text);
                            
                            if (wordIndex !== -1) {
                                nextText = targetText.slice(0, nextText.length + wordIndex + word.text.length);
                            } else {
                                // 兜底：如果没找到，至少前进一步
                                nextText = targetText.slice(0, nextText.length + Math.max(1, word.text.length));
                            }
                        }
                    } else {
                        // 时间还没到
                        break;
                    }
                }
                
                return nextText;
            });
            animationFrameId = requestAnimationFrame(animateTyping);
        };

        animationFrameId = requestAnimationFrame(animateTyping);
        return () => cancelAnimationFrame(animationFrameId);
    }, [isAISpeaking, realtimeVoiceEngineRef]);

    const setAiReplyText = (text: string) => {
        targetAiReplyTextRef.current = text;
        if (text === "") {
            setDisplayedAiReplyText("");
            wordMarkersQueueRef.current = [];
            // 重置平滑时钟
            smoothedPlaybackTimeMsRef.current = 0;
            // 如果清空文本，通常意味着开启了新的一轮或者被打断
            realtimeVoiceEngineRef.resetPlaybackTimer?.();
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            console.log('[UI] Unmounting useWhisperEngine, stopping engine...');
            if (onUnmountRef.current) {
                const buffered = realtimeVoiceEngineRef.getBufferedContent();
                console.log('[UI] useWhisperEngine cleanup: capturing buffered content:', buffered);
                onUnmountRef.current(buffered);
            }
            realtimeVoiceEngineRef.stop();
        };
    }, []);

    // Connection Timeout
    useEffect(() => {
        let timeoutId: NodeJS.Timeout;
        if (isConnecting) {
            timeoutId = setTimeout(async () => {
                console.warn('[UI] Connection timed out');
                setIsConnecting(false);
                flushSync(() => setConversationActive(false));
                try {
                    await realtimeVoiceEngineRef.stop();
                } catch (e) {
                    console.error('[UI] Failed to stop on timeout:', e);
                }
                toast.error(t('whisper.connectionTimeout'));
                setStatus('idle');
            }, 30000);
        }
        return () => clearTimeout(timeoutId);
    }, [isConnecting, realtimeVoiceEngineRef, t]);

    // Engine Callbacks
    useEffect(() => {
        realtimeVoiceEngineRef.onSpeechStart?.(() => {
            setStatus("recognizing");
            setIsUserSpeaking(true);
            // 用户开始说话，重置上一轮的播放计时和进度
            smoothedPlaybackTimeMsRef.current = 0;
            realtimeVoiceEngineRef.resetPlaybackTimer?.();
        });

        realtimeVoiceEngineRef.onSpeechEnd?.(() => {
            setIsUserSpeaking(false);
        });

        realtimeVoiceEngineRef.onQuotaExceeded?.((message) => {
            console.warn('[UI] Quota exceeded:', message);
            if (onDurationLimit) {
                onDurationLimit(message);
            }
            // Stop logic
            setStatus("idle");
            setConversationActive(false);
            setIsConnecting(false);
            onStopConversation?.();
        });

        realtimeVoiceEngineRef.onRateLimit?.((retryAfter) => {
            console.warn('[UI] Rate limit exceeded:', retryAfter);
            toast.warning(t('common.rateLimit', { seconds: retryAfter }));
            setStatus("idle");
            setConversationActive(false);
            setIsConnecting(false);
        });

        realtimeVoiceEngineRef.onSessionReady?.((id: string | undefined) => {
            console.log('[UI] Session ready, conversation_id:', id);
            setIsConnecting(false);
            setIsReconnecting(false);
            if (onSessionReady) {
                onSessionReady(id);
            }
        });

        realtimeVoiceEngineRef.onAISpeakingStateChange?.((isSpeaking) => {
            setIsAISpeaking(isSpeaking);
            if (onAISpeakingStateChange) {
                onAISpeakingStateChange(isSpeaking);
            }
        });

        realtimeVoiceEngineRef.onSuggestions?.((hints) => {
            setSuggestions(hints);
            if (onSuggestions) {
                onSuggestions(hints);
            }
        });

        realtimeVoiceEngineRef.onWordBoundary?.((data) => {
            wordMarkersQueueRef.current.push(data);
        });

        realtimeVoiceEngineRef.onConnectionLost?.((retryCount, delay) => {
            console.warn(`[UI] Connection lost, attempt ${retryCount} in ${delay}ms`);
            setIsReconnecting(true);
            // Modal in WhisperPage will handle the UI
        });

        realtimeVoiceEngineRef.onVisibilityResume?.(() => {
            console.log('[UI] Visibility resume: AudioManager recreated, confirming active state');
            setStatus("recognizing");
            setIsReconnecting(false);
        });

        realtimeVoiceEngineRef.onError?.((message) => {
            console.error('[UI] Engine error:', message);
            setIsConnecting(false);
            setIsReconnecting(false);
            setStatus("idle");
            setConversationActive(false);
            toast.error(message || t('whisper.connectionError'));
        });
    }, [realtimeVoiceEngineRef, t, onStopConversation, onDurationLimit, onSessionReady, onAISpeakingStateChange, onSuggestions]);

    // Actions
    const startListening = useCallback(async (
        personaId: string | undefined,
        speakerId: string | undefined,
        gender: string,
        systemRole: string | undefined,
        options?: { intent?: 'new' | 'resume'; conversationId?: string; textOnly?: boolean }
    ) => {
        try {
            console.log(`[Engine] startListening - Lang: ${language}, Persona: ${personaId}, Speaker: ${speakerId}, Gender: ${gender}, Conv: ${options?.conversationId}, textOnly: ${options?.textOnly === true}`);
            setLastText("");
            setAiReplyText("");
            setIsConnecting(true);
            sessionStartTimeRef.current = Date.now();

            const provider = getProviderByLanguage(language);
            realtimeVoiceEngineRef.setLanguage(language);
            realtimeVoiceEngineRef.setProvider(provider);

            const user = loadAuthUser();
            const fallbackName = user?.nickname || (user?.full_name ? user.full_name.split(' ')[0] : undefined);

            await realtimeVoiceEngineRef.start({
                personaId,
                speakerId,
                gender,
                systemRole,
                intent: options?.intent || 'new',
                userName: getUserDisplayName(fallbackName),
                conversationId: options?.conversationId,
                textOnly: options?.textOnly === true,
            });
            setStatus(options?.textOnly === true ? "silent" : "recognizing");
            setConversationActive(true);
            setIsConnecting(false);
            setIsReconnecting(false); // 重连成功或手动开始时重置
        } catch (e) {
            console.error('[Engine] Start failed:', e);
            setIsConnecting(false);
            setConversationActive(false);

            try {
                const isAuth = await isAuthenticated();
                if (!isAuth) {
                    console.warn('[Engine] Start failed due to auth, logging out...');
                    toast.error(t('auth.sessionExpired') || "Session expired. Please log in again.");
                    await logout();
                    navigate('/splash');
                    return;
                }
            } catch (authChkErr) {
                console.error('[Engine] Failed auth check after error:', authChkErr);
            }
            
            toast.error(t('whisper.connectionFailed') || "Connection failed. Please try again.");
        }
    }, [realtimeVoiceEngineRef, language, navigate, t]);

    const stopListening = useCallback(async () => {
        try {
            console.log(`[Engine] stopListening`);
            await realtimeVoiceEngineRef.stop();
            setStatus("idle");
            setIsUserSpeaking(false);
            setIsConnecting(false);
            setIsReconnecting(false);
        } catch (e) {
            console.error('[Engine] Stop failed:', e);
        }
    }, [realtimeVoiceEngineRef]);

    const getCurrentSessionDuration = useCallback(() => {
        if (!sessionStartTimeRef.current) return 0;
        return Math.floor((Date.now() - sessionStartTimeRef.current) / 1000);
    }, []);

    const resetSessionTimer = useCallback(() => {
        sessionStartTimeRef.current = null;
    }, []);

    return {
        status,
        setStatus,
        lastText,
        setLastText,
        aiReplyText: displayedAiReplyText,
        setAiReplyText,
        conversationActive,
        setConversationActive,
        isConnecting,
        isReconnecting,
        isUserSpeaking,
        isAISpeaking,
        suggestions,
        setSuggestions,
        startListening,
        stopListening,
        getCurrentSessionDuration,
        resetSessionTimer,
        setUserName: realtimeVoiceEngineRef.setUserName.bind(realtimeVoiceEngineRef),
        engine: realtimeVoiceEngineRef
    };
}
